import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useMembers, useCreateMember } from "@/hooks/useMembers";
import { useAddTeamMember, useTeamMembers } from "@/hooks/useTeamMembers";
import { useToast } from "@/hooks/useToast";
import { MEMBER_ROLES, INSTRUMENTS } from "@/utils/constants";
import { X, Search, UserPlus, ChevronDown, ChevronUp } from "lucide-react";
import type { Tables } from "@/types/database";

interface TeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
}

interface SelectedMember {
  id: string;
  name: string;
  isNew: boolean;
  roleInTeam: string;
  instrument: string;
  customInstrument: string;
  expanded: boolean;
}

export function TeamMemberDialog({
  open,
  onClose,
  teamId,
}: TeamMemberDialogProps) {
  const { data: allMembers = [] } = useMembers();
  const { data: currentTeamMembers = [] } = useTeamMembers(teamId);
  const addTeamMember = useAddTeamMember();
  const createMember = useCreateMember();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const availableMembers = useMemo(() => {
    const currentMemberIds = new Set(currentTeamMembers.map((m) => m.user_id));
    const selectedMemberIds = new Set(selectedMembers.map((m) => m.id));
    return allMembers.filter(
      (m) => !currentMemberIds.has(m.id) && !selectedMemberIds.has(m.id),
    );
  }, [allMembers, currentTeamMembers, selectedMembers]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return availableMembers.slice(0, 8);
    const query = searchQuery.toLowerCase();
    return availableMembers
      .filter(
        (m) =>
          m.full_name?.toLowerCase().includes(query) ||
          m.email?.toLowerCase().includes(query),
      )
      .slice(0, 8);
  }, [availableMembers, searchQuery]);

  const exactMatch = useMemo(
    () =>
      availableMembers.some(
        (m) => m.full_name?.toLowerCase() === searchQuery.toLowerCase().trim(),
      ),
    [availableMembers, searchQuery],
  );

  const canCreateNew =
    searchQuery.trim().length >= 2 &&
    !exactMatch &&
    !selectedMembers.some(
      (m) => m.name.toLowerCase() === searchQuery.toLowerCase().trim(),
    );

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSelectedMembers([]);
      setShowSuggestions(false);
    }
  }, [open]);

  const addExistingMember = (member: Tables<"users">) => {
    setSelectedMembers([
      ...selectedMembers,
      {
        id: member.id,
        name: member.full_name || member.email || "Sans nom",
        isNew: false,
        roleInTeam: "",
        instrument: "",
        customInstrument: "",
        expanded: false,
      },
    ]);
    setSearchQuery("");
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  };

  const addNewMember = () => {
    const name = searchQuery.trim();
    if (!name) return;
    setSelectedMembers([
      ...selectedMembers,
      {
        id: `new-${Date.now()}`,
        name,
        isNew: true,
        roleInTeam: "",
        instrument: "",
        customInstrument: "",
        expanded: false,
      },
    ]);
    setSearchQuery("");
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  };

  const removeMember = (id: string) => {
    setSelectedMembers(selectedMembers.filter((m) => m.id !== id));
  };

  const toggleExpanded = (id: string) => {
    setSelectedMembers(
      selectedMembers.map((m) =>
        m.id === id ? { ...m, expanded: !m.expanded } : m,
      ),
    );
  };

  const updateMember = (id: string, updates: Partial<SelectedMember>) => {
    setSelectedMembers(
      selectedMembers.map((m) => (m.id === id ? { ...m, ...updates } : m)),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedMembers.length === 0) {
      toast({
        title: "Aucun membre sélectionné",
        description: "Recherchez et sélectionnez au moins un membre",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let addedCount = 0;
      for (const member of selectedMembers) {
        let userId = member.id;

        if (member.isNew) {
          const newMember = await createMember.mutateAsync({
            id: crypto.randomUUID(),
            full_name: member.name,
            email: `member-${crypto.randomUUID()}@temp.local`,
            role: "musician",
          });
          userId = newMember.id;
        }

        await addTeamMember.mutateAsync({
          team_id: teamId,
          user_id: userId,
          role_in_team: member.roleInTeam || null,
          instrument:
            member.instrument === "Autre"
              ? member.customInstrument || null
              : member.instrument || null,
          is_leader: false,
        });
        addedCount++;
      }

      toast({
        title:
          addedCount === 1 ? "Membre ajouté" : `${addedCount} membres ajoutés`,
      });
      handleClose();
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter les membres",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    setSelectedMembers([]);
    setShowSuggestions(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Ajouter des membres</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Rechercher ou créer un membre..."
                className="pl-10"
                autoComplete="off"
              />
            </div>

            {showSuggestions && (searchQuery || filteredMembers.length > 0) && (
              <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-y-auto">
                {filteredMembers.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => addExistingMember(member)}
                    className="w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-2"
                  >
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {(member.full_name ||
                        member.email ||
                        "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {member.full_name || "Sans nom"}
                      </div>
                      {member.email && (
                        <div className="text-xs text-muted-foreground truncate">
                          {member.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))}

                {canCreateNew && (
                  <>
                    {filteredMembers.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    <button
                      type="button"
                      onClick={addNewMember}
                      className="w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-2 text-primary"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <UserPlus className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">
                          Créer "{searchQuery.trim()}"
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Nouveau membre
                        </div>
                      </div>
                    </button>
                  </>
                )}

                {filteredMembers.length === 0 &&
                  !canCreateNew &&
                  searchQuery && (
                    <div className="px-3 py-4 text-center text-muted-foreground text-sm">
                      Aucun membre trouvé
                    </div>
                  )}
              </div>
            )}
          </div>

          {selectedMembers.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                {selectedMembers.length} membre
                {selectedMembers.length > 1 ? "s" : ""} sélectionné
                {selectedMembers.length > 1 ? "s" : ""}
              </Label>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {selectedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    <div className="flex items-center gap-2 p-2 bg-accent/30">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium shrink-0">
                        {member.name[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate flex items-center gap-1">
                          {member.name}
                          {member.isNew && (
                            <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                              nouveau
                            </span>
                          )}
                        </div>
                        {(member.roleInTeam || member.instrument) && (
                          <div className="text-xs text-muted-foreground truncate">
                            {[
                              member.roleInTeam,
                              member.instrument === "Autre"
                                ? member.customInstrument
                                : member.instrument,
                            ]
                              .filter(Boolean)
                              .join(" • ")}
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpanded(member.id)}
                        className="h-8 w-8 p-0"
                      >
                        {member.expanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(member.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {member.expanded && (
                      <div className="p-3 space-y-3 border-t bg-background">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Rôle</Label>
                            <Select
                              value={member.roleInTeam}
                              onValueChange={(v) =>
                                updateMember(member.id, { roleInTeam: v })
                              }
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Optionnel" />
                              </SelectTrigger>
                              <SelectContent>
                                {MEMBER_ROLES.map((role) => (
                                  <SelectItem key={role} value={role}>
                                    {role}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Instrument</Label>
                            <Select
                              value={member.instrument}
                              onValueChange={(v) =>
                                updateMember(member.id, { instrument: v })
                              }
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Optionnel" />
                              </SelectTrigger>
                              <SelectContent>
                                {INSTRUMENTS.map((instr) => (
                                  <SelectItem key={instr} value={instr}>
                                    {instr}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {member.instrument === "Autre" && (
                          <div className="space-y-1">
                            <Label className="text-xs">Préciser</Label>
                            <Input
                              value={member.customInstrument}
                              onChange={(e) =>
                                updateMember(member.id, {
                                  customInstrument: e.target.value,
                                })
                              }
                              placeholder="Harpe, Accordéon..."
                              className="h-9"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || selectedMembers.length === 0}
            >
              {isSubmitting
                ? "Ajout..."
                : selectedMembers.length === 0
                  ? "Ajouter"
                  : `Ajouter ${selectedMembers.length} membre${selectedMembers.length > 1 ? "s" : ""}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
